import React, { useState } from 'react';
import { EditorContents, getEditorsForUser } from './Editor';
import { useCurrentUser } from '../common/withUser';
import { htmlToTextDefault } from '@/lib/htmlToText';
import ForumIcon from "../common/ForumIcon";
import { defineStyles, useStyles } from '../hooks/useStyles';
import { useEffectOnce } from '../hooks/useEffectOnce';

const styles = defineStyles("LocalStorageCheck", (theme: ThemeType) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: 10,
    fontFamily: theme.typography.commentStyle.fontFamily,
    color: theme.palette.text.primaryAlert,
    fontSize: 14,
    lineHeight: '18px',
    fontWeight: '500',
    padding: '10px 8px',
    borderRadius: 4,
    backgroundColor: theme.palette.background.primaryTranslucent,
    marginBottom: 8,
    
    "& a": {
      '&:hover': {
        color: theme.palette.primary.dark,
        opacity: 1
      }
    }
  },
  restoreLink: {
    color: theme.palette.text.primaryAlert,
    whiteSpace: 'nowrap',
    paddingLeft: 6,
    paddingRight: 2
  },
  restoreBody: {
    maxHeight: '1.5em',
    lineHeight: '1.5em',
    fontSize: '1.1rem',
    overflow: 'hidden',
    color: theme.palette.grey[500],
    padding: '0 4px',
  },
  closeIcon: {
    fontSize: 16,
    cursor: 'pointer',
    marginLeft: 'auto',
    '&:hover': {
      color: theme.palette.primary.dark,
    }
  }
}));

type RestorableState = {
  savedDocument: EditorContents,
}
const restorableStateHasMetadata = (savedState: any) => {
  return typeof savedState === "object"
}
type GetLocalStorageHandlers = (editorType?: string) => any;

const getRestorableState = (currentUser: UsersCurrent|null, getLocalStorageHandlers: GetLocalStorageHandlers): RestorableState|null => {
  const editors = getEditorsForUser(currentUser)

  for (const editorType of editors) {
    const savedState = getLocalStorageHandlers(editorType).get();
    if (savedState) {
      if (restorableStateHasMetadata(savedState)) {
        // Skip corrupt saved states that are missing an editor type
        if (savedState.type) {
          return {
            savedDocument: savedState,
          }
        }
        continue;
      }
      return {
        savedDocument: {type: editorType, value: savedState}
      }
    }
  }
  return null;
};

type LocalStorageCheckProps = {
  getLocalStorageHandlers: GetLocalStorageHandlers,
  onRestore: (newState: EditorContents) => void,
  getNewPostLocalStorageHandlers: GetLocalStorageHandlers,
  onRestoreNewPostLegacy: (newState: EditorContents) => void,
}

const LocalStorageCheck = (props: LocalStorageCheckProps) => {
  const {getLocalStorageHandlers, getNewPostLocalStorageHandlers} = props;
  const [restorableState, setRestorableState] = useState<{restorableState: RestorableState|null, newPostRestorableState: RestorableState|null} | null>(null);
  const currentUser = useCurrentUser();
  
  useEffectOnce(() => {
    const restorableState = getRestorableState(currentUser, getLocalStorageHandlers);
    const newPostRestorableState = getRestorableState(currentUser, getNewPostLocalStorageHandlers);
    if (restorableState || newPostRestorableState) {
      setRestorableState({
        restorableState,
        newPostRestorableState,
      });
    }
  });

  const restorableDocument = restorableState?.restorableState?.savedDocument ?? null;
  const newPostRestorableDocument = restorableState?.newPostRestorableState?.savedDocument ?? null;
  
  if (!restorableDocument && !newPostRestorableDocument)
    return null;

  return <LocalStorageCheckVisible
    {...props} restorableDocument={restorableDocument} newPostRestorableDocument={newPostRestorableDocument}
    clearRestorableState={() => setRestorableState(null)}
  />
}

const LocalStorageCheckVisible = (props: LocalStorageCheckProps & {
  restorableDocument: EditorContents|null
  newPostRestorableDocument: EditorContents|null
  clearRestorableState: () => void
}) => {
  const {onRestore, onRestoreNewPostLegacy, restorableDocument, newPostRestorableDocument, clearRestorableState} = props;
  const classes = useStyles(styles);

  const displayedRestore = restorableDocument ? htmlToTextDefault(restorableDocument.value ?? '') : null;
  const legacyRestored = newPostRestorableDocument ? htmlToTextDefault(newPostRestorableDocument.value ?? '') : null;

  return <div className={classes.root}>
    <div>
      <a className={classes.restoreLink} onClick={() => {
        clearRestorableState();
        if (restorableDocument) {
          onRestore(restorableDocument);
        } else if (newPostRestorableDocument) {
          onRestoreNewPostLegacy(newPostRestorableDocument);
        } else {
          // eslint-disable-next-line no-console
          console.error("Error restoring from localStorage");
        }
      }}>Restore Autosave</a>
    </div>
    <div className={classes.restoreBody}> {displayedRestore || legacyRestored} </div>

    <ForumIcon icon="Close" className={classes.closeIcon} onClick={() => clearRestorableState()}/>
  </div>
}

export default LocalStorageCheck;


